import { toLatLng } from 'features/Responder/features/Tours';
import {
  FaDrawPolygon,
  FaInfoCircle,
  FaMapMarkerAlt,
  FaPlay,
  FaRoute,
  FaSignInAlt,
  FaSignOutAlt,
  FaStickyNote,
  FaStop,
  FaUndoAlt,
  FaUserClock,
} from 'react-icons/fa';

const normalizeLabel = (value) => {
  if (!value || typeof value !== 'string') return '';
  const cleaned = value.replace(/[_-]+/g, ' ').trim();
  if (!cleaned) return '';
  return cleaned
    .toLowerCase()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const normalizeEventType = (value) => {
  if (!value || typeof value !== 'string') return '';
  return value
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, '-');
};

const parseJsonMaybe = (value) => {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const toFiniteNumber = (value) => {
  const next = Number.parseFloat(value);
  return Number.isFinite(next) ? next : null;
};

const isValidLatLng = (lat, lng) =>
  Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;

export const hasValidEventLatLng = (value) => {
  const candidate = value?.latLng ?? value;
  if (!candidate || typeof candidate !== 'object') return false;
  const lat = Number(candidate.lat);
  const lng = Number(candidate.lng);
  return isValidLatLng(lat, lng);
};

const fromPair = (first, second, { preferGeoJson = false } = {}) => {
  const a = toFiniteNumber(first);
  const b = toFiniteNumber(second);
  if (a == null || b == null) return null;
  const latLng = { lat: a, lng: b };
  const lngLat = { lat: b, lng: a };
  const latLngValid = isValidLatLng(latLng.lat, latLng.lng);
  const lngLatValid = isValidLatLng(lngLat.lat, lngLat.lng);

  if (preferGeoJson && lngLatValid) return lngLat;
  if (latLngValid && !lngLatValid) return latLng;
  if (lngLatValid && !latLngValid) return lngLat;
  if (latLngValid) return latLng;
  if (lngLatValid) return lngLat;
  return null;
};

const resolveNamedLatLng = (value) => {
  if (!value || typeof value !== 'object') return null;
  return fromPair(
    value.lat ?? value.latitude ?? value.y ?? value.location?.lat ?? value.location?.latitude,
    value.lng ??
      value.lon ??
      value.longitude ??
      value.x ??
      value.location?.lng ??
      value.location?.lon ??
      value.location?.longitude,
  );
};

const resolveGeoJsonLatLng = (value) => {
  if (!value || typeof value !== 'object') return null;
  const geometry = value?.type === 'Feature' ? value?.geometry : value;
  const coords =
    geometry?.coordinates ??
    geometry?.coord ??
    geometry?.coords ??
    value?.coordinates ??
    value?.coord ??
    value?.coords ??
    null;
  if (!Array.isArray(coords) || coords.length < 2) return null;
  return fromPair(coords[0], coords[1], { preferGeoJson: true });
};

export const EVENT_KIND_DEFINITIONS = {
  note: {
    key: 'note',
    label: 'Notes',
    color: '#f5a623',
    Icon: FaStickyNote,
  },
  clock_in: {
    key: 'clock_in',
    label: 'Clock In',
    color: '#2ecc71',
    Icon: FaSignInAlt,
  },
  clock_out: {
    key: 'clock_out',
    label: 'Clock Out',
    color: '#e74c3c',
    Icon: FaSignOutAlt,
  },
  tour_started: {
    key: 'tour_started',
    label: 'Tour Started',
    color: '#27ae60',
    Icon: FaPlay,
  },
  tour_stopped: {
    key: 'tour_stopped',
    label: 'Tour Stopped',
    color: '#c0392b',
    Icon: FaStop,
  },
  location: {
    key: 'location',
    label: 'Location Update',
    color: '#3498db',
    Icon: FaMapMarkerAlt,
  },
  loitering: {
    key: 'loitering',
    label: 'Loitering',
    color: '#f39c12',
    Icon: FaUserClock,
  },
  off_route: {
    key: 'off_route',
    label: 'Off Route',
    color: '#e67e22',
    Icon: FaRoute,
  },
  returned: {
    key: 'returned',
    label: 'Returned To Route',
    color: '#1abc9c',
    Icon: FaUndoAlt,
  },
  geofence: {
    key: 'geofence',
    label: 'Geofence',
    color: '#9b59b6',
    Icon: FaDrawPolygon,
  },
  default: {
    key: 'default',
    label: 'Other',
    color: '#95a5a6',
    Icon: FaInfoCircle,
  },
};

export const EVENT_KIND_ORDER = [
  'note',
  'clock_in',
  'clock_out',
  'tour_started',
  'tour_stopped',
  'location',
  'loitering',
  'off_route',
  'returned',
  'geofence',
  'default',
];

export const resolveEventKind = (eventType, meta = {}) => {
  const normalized = normalizeEventType(eventType);
  if (meta?.note || normalized.includes('note')) return 'note';
  if (
    normalized === 'personnel-clocked-in' ||
    normalized === 'personnel-clock-in' ||
    normalized === 'clocked-in' ||
    normalized === 'clock-in'
  )
    return 'clock_in';
  if (
    normalized === 'personnel-clocked-out' ||
    normalized === 'personnel-clock-out' ||
    normalized === 'clocked-out' ||
    normalized === 'clock-out'
  )
    return 'clock_out';
  if (normalized === 'personnel-started-tour' || normalized === 'tour-started')
    return 'tour_started';
  if (normalized === 'personnel-stopped-tour' || normalized === 'tour-stopped')
    return 'tour_stopped';
  if (
    normalized === 'personnel-location-updated' ||
    normalized === 'location-updated' ||
    normalized.includes('location')
  )
    return 'location';
  if (normalized === 'loitering-detected' || normalized.includes('loitering')) return 'loitering';
  if (normalized === 'veered-off-route' || normalized.includes('off-route')) return 'off_route';
  if (normalized === 'returned-to-route' || normalized.includes('returned')) return 'returned';
  if (/^(passed|entered|exited)-/.test(normalized)) return 'geofence';
  return 'default';
};

export const getEventKindDefinition = (kind) =>
  EVENT_KIND_DEFINITIONS[kind] || EVENT_KIND_DEFINITIONS.default;

export const formatEventTimestamp = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const resolveLatLng = (event) => {
  if (!event) return null;
  const directGeo = resolveGeoJsonLatLng(event);
  if (directGeo) return directGeo;
  const directNamed = resolveNamedLatLng(event);
  if (directNamed) return directNamed;
  const direct = toLatLng(event);
  if (direct) return direct;
  const meta = parseJsonMaybe(event.meta) || null;
  const metaGeo = resolveGeoJsonLatLng(meta);
  if (metaGeo) return metaGeo;
  const metaNamed = resolveNamedLatLng(meta);
  if (metaNamed) return metaNamed;
  const metaLatLng = toLatLng(meta);
  if (metaLatLng) return metaLatLng;
  const metaNote = parseJsonMaybe(meta?.note);
  const metaNoteGeo = resolveGeoJsonLatLng(metaNote);
  if (metaNoteGeo) return metaNoteGeo;
  const metaNoteNamed = resolveNamedLatLng(metaNote);
  if (metaNoteNamed) return metaNoteNamed;
  const metaNoteLatLng = toLatLng(metaNote);
  if (metaNoteLatLng) return metaNoteLatLng;
  const metaLocationCandidate = meta?.location || meta?.loc || meta?.position || meta?.coords;
  const metaLocation =
    resolveGeoJsonLatLng(metaLocationCandidate) ||
    resolveNamedLatLng(metaLocationCandidate) ||
    toLatLng(metaLocationCandidate) ||
    resolveGeoJsonLatLng(meta?.geometry) ||
    resolveNamedLatLng(meta?.geometry) ||
    toLatLng(meta?.geometry);
  if (metaLocation) return metaLocation;
  const noteNamed = resolveNamedLatLng(event?.note);
  if (noteNamed) return noteNamed;
  const noteGeo = resolveGeoJsonLatLng(event?.note);
  if (noteGeo) return noteGeo;
  const noteLatLng = toLatLng(event?.note);
  if (noteLatLng) return noteLatLng;
  const locationCandidate = event.location || event.loc || event.position || event.coords;
  const locationNamed = resolveNamedLatLng(locationCandidate);
  if (locationNamed) return locationNamed;
  const locationGeo = resolveGeoJsonLatLng(locationCandidate);
  if (locationGeo) return locationGeo;
  const location = toLatLng(event.location || event.loc || event.position || event.coords);
  if (location) return location;
  return null;
};

const pickEventTimestamp = (event) =>
  event?.time ??
  event?.timestamp ??
  event?.occurredAt ??
  event?.createdAt ??
  parseJsonMaybe(event?.meta)?.timestamp ??
  null;

const buildEventDetails = (event, meta) => {
  if (!event) return '';
  const metaNote = parseJsonMaybe(meta?.note);
  if (typeof metaNote === 'string') return metaNote;
  if (metaNote?.note) return metaNote.note;
  if (metaNote?.message) return metaNote.message;
  if (typeof event.note === 'string') return event.note;
  if (event.note?.note) return event.note.note;
  if (event.note?.message) return event.note.message;
  if (meta.message) return meta.message;
  if (event.message) return event.message;
  if (meta.idleTime != null) return `Idle time: ${meta.idleTime}`;
  if (meta.duration != null) return `Duration: ${meta.duration}`;
  return event.details || event.description || '';
};

const resolveNoteText = (meta = {}, event = {}) => {
  const metaNote = parseJsonMaybe(meta?.note);
  if (typeof metaNote === 'string') return metaNote;
  if (metaNote?.note) return metaNote.note;
  if (metaNote?.message) return metaNote.message;
  if (typeof event.note === 'string') return event.note;
  if (event.note?.note) return event.note.note;
  if (event.note?.message) return event.note.message;
  return '';
};

export const normalizeRunEvent = (event, index = 0) => {
  if (!event || typeof event !== 'object') return null;
  const eventType = event.event || event.type || event.status || event.message || 'event';
  const timestamp = pickEventTimestamp(event);
  const meta = parseJsonMaybe(event.meta) || {};
  const kind = resolveEventKind(eventType, meta);
  const label = normalizeLabel(eventType || 'Event');
  const id = event.id ?? event.eventId ?? `${eventType}-${timestamp || index}`;
  return {
    id,
    kind,
    label: label || 'Event',
    eventType,
    timestamp,
    meta,
    note: resolveNoteText(meta, event),
    details: buildEventDetails(event, meta),
    latLng: resolveLatLng(event),
    raw: event,
  };
};

export const normalizeRunEvents = (events = []) =>
  (Array.isArray(events) ? events : [])
    .map((event, index) => normalizeRunEvent(event, index))
    .filter(Boolean)
    .sort((a, b) => {
      const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return bTime - aTime;
    });

const normalizeNoteEntry = (note, index = 0, runId = null) => {
  if (!note || typeof note !== 'object') return null;
  const latLng =
    resolveGeoJsonLatLng(note) ||
    resolveNamedLatLng(note) ||
    toLatLng(note) ||
    resolveGeoJsonLatLng(note.meta) ||
    resolveNamedLatLng(note.meta) ||
    toLatLng(note.meta) ||
    null;
  if (!latLng) return null;
  const timestamp = note.time || note.timestamp || note.createdAt || note.created_at || null;
  const noteText = note.note || note.message || note.meta?.note || '';
  return {
    id: note.id ?? `note-${runId ?? 'run'}-${index}`,
    kind: 'note',
    label: 'Activity Note',
    eventType: 'run_note_added',
    timestamp,
    meta: {
      note: noteText,
      lat: latLng.lat,
      lng: latLng.lng,
    },
    note: noteText,
    details: noteText,
    latLng,
    raw: note,
  };
};

export const mergeSupplementalNotes = (events = [], notes = [], runId = null) => {
  if (!Array.isArray(notes) || notes.length === 0) return events;
  const normalizedNotes = notes
    .map((note, index) => normalizeNoteEntry(note, index, runId))
    .filter(Boolean);
  if (!normalizedNotes.length) return events;
  const existingIds = new Set(events.map((event) => `${event.id}`));
  const merged = [...events];
  normalizedNotes.forEach((note) => {
    const key = `${note.id}`;
    if (!existingIds.has(key)) {
      existingIds.add(key);
      merged.push(note);
    }
  });
  return merged.sort((a, b) => {
    const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
    const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
    return bTime - aTime;
  });
};

export const getEventKindCounts = (events = []) => {
  const counts = {};
  events.forEach((event) => {
    if (!event?.kind) return;
    counts[event.kind] = (counts[event.kind] || 0) + 1;
  });
  return counts;
};

export const getUniqueEventKinds = (events = []) =>
  Array.from(
    new Set((Array.isArray(events) ? events : []).map((event) => event?.kind || 'default')),
  );
